// addTable — VQE-Edit edit-action: append a FROM item via SR-Edit's
// `add-from-item` op. Domain knowledge owned here: how to format a
// `Table` (or a free-form text) as a SQL relation reference, and how to
// position it in the existing FROM list. The actual byte-level surgery is
// delegated to `commitEdit` (the only module that talks to Monaco).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — add/remove FROM
// item.

import type { QualifiedName } from '../../../../sql-grammar/model';
import type { Table } from '../../../../sql-resolver/types/schema';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';
import { formatCatalogTableRef } from './fromItemHelpers';

export type AddTableOptions = {
  /**
   * If set, insert AFTER this 0-based FROM-list index. Omit to append after
   * the last item (SR-Edit's default behavior). Negative inserts before the
   * first item.
   */
  readonly afterIndex?: number;
  /**
   * Override the default `formatCatalogTableRef` output — used when the popover
   * already prepared the textual form (e.g. user typed a custom expression
   * like `films AS f` or a schema-qualified relation).
   */
  readonly textOverride?: string;
};

export async function addTable(
  table: Table,
  ctx: CommitEditCtx,
  options: AddTableOptions = {},
): Promise<void> {
  const text = options.textOverride ?? formatCatalogTableRef(table, ctx.service.engineId);
  await commitEdit(
    {
      op: 'add-from-item',
      text,
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}

/**
 * Append an arbitrary relation expression (e.g. `films f`, `(SELECT 1) AS x`).
 * Bypasses the `Table → text` conversion. Used by the schema-tree drop
 * handler when the dropped payload already carries a fully-qualified text.
 *
 * The commit itself no longer rejects (P12 §4.2) — forward its `CommitOutcome`
 * so the caller (the drag-drop handler in `FromSection.tsx`) only fires
 * success analytics on `applied`, never on a failed or merely-staged commit.
 */
export async function addRelationExpression(
  expression: string,
  ctx: CommitEditCtx,
  options: { readonly afterIndex?: number } = {},
): Promise<CommitOutcome> {
  return commitEdit(
    {
      op: 'add-from-item',
      text: expression,
      ...(options.afterIndex !== undefined ? { afterIndex: options.afterIndex } : {}),
    },
    ctx,
  );
}

/**
 * Re-export for ergonomic single-import usage by the drag-drop wire.
 */
export type { QualifiedName };
